import { Phone, Calculator as CalcIcon, ArrowDown } from "lucide-react";
import { COMPANY } from "@/lib/content";
import ShopSearch from "@/sections/ShopSearch";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-24 md:pb-32 overflow-hidden"
    >
      {/* Background tire image with light fade overlay */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/21694/pexels-photo.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute right-0 top-0 h-full w-full lg:w-[60%] object-cover object-center opacity-40 [mask-image:linear-gradient(to_left,black_35%,transparent_100%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#F0F1F5] via-[#F0F1F5]/75 to-[#F0F1F5]/20" />
        <div className="absolute inset-0 grid-bg opacity-50" />
      </div>

      {/* Outline marquee */}
      <div className="absolute top-24 left-0 right-0 select-none overflow-hidden pointer-events-none">
        <div className="marquee-track whitespace-nowrap">
          {Array.from({ length: 6 }).map((_, i) => (
            <span
              key={i}
              className="font-display text-[140px] md:text-[220px] font-black uppercase tracking-tighter text-outline px-8"
            >
              NONSTOP · MOBILGUMISZERVÍZ ·
            </span>
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8 fade-up">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-violet-500/30 bg-violet-500/5 backdrop-blur-sm mb-8">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-green-400 pulse-dot"></span>
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-gray-900/90 font-semibold">
              Most is dolgozunk · Budapest
            </span>
          </div>

          <h1 className="font-display font-black uppercase tracking-tighter text-gray-900 text-[42px] sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.92]">
            Defekt? Gumicsere?
            <br />
            <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-violet-600 bg-clip-text text-transparent">
              Mi jövünk
            </span>{" "}
            hozzád.
          </h1>

          <p className="mt-8 text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
            A <span className="text-gray-900 font-semibold">{COMPANY.name}</span> Budapest 0–24-es
            mobil gumiszervíze. Otthonod, irodád vagy az út szélén —
            <span className="text-violet-600"> 60 percen belül</span> a helyszínen vagyunk.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              data-testid="hero-call-btn"
              href={`tel:${COMPANY.phoneRaw}`}
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold uppercase tracking-wider text-sm md:text-base rounded-sm hover:from-blue-500 hover:to-violet-500 transition-all glow-blue"
            >
              <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
              Hívd most: {COMPANY.phone}
            </a>
            <a
              data-testid="hero-calculator-btn"
              href="#arkalkulator"
              className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-gray-300 bg-gray-100/5 backdrop-blur-md text-gray-900 font-bold uppercase tracking-wider text-sm md:text-base rounded-sm hover:bg-gray-100/10 hover:border-violet-400/50 transition-all"
            >
              <CalcIcon className="w-5 h-5" />
              Árkalkulátor
            </a>
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-gray-500">
            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>Budapest + agglomeráció</div>
            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-violet-400 rounded-full"></div>Fix árak, nincs rejtett költség</div>
            <div className="flex items-center gap-2"><div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>Garanciával</div>
          </div>

          {/* Used tire shop search */}
          <ShopSearch />
        </div>

        {/* Side info card */}
        <div className="lg:col-span-4 fade-up" style={{ animationDelay: "0.25s" }}>
          <div className="relative border border-blue-300/60 bg-white p-6 md:p-8 rounded-md shadow-[0_25px_70px_-15px_rgba(37,99,235,0.35),0_4px_16px_rgba(10,10,15,0.08)]">
            <div className="absolute -top-px left-0 right-0 h-1 rounded-t-md bg-gradient-to-r from-blue-500 via-violet-500 to-blue-500" />
            <div className="text-[11px] uppercase tracking-[0.25em] text-violet-600 font-bold">Élő státusz</div>
            <div className="mt-4 font-display text-4xl md:text-5xl font-black tracking-tight">
              60<span className="text-base ml-1 text-gray-500">percen belül</span>
            </div>
            <div className="text-sm text-gray-500 mt-1">Kiérkezés Budapesten</div>

            <div className="mt-8 space-y-4">
              <div className="flex items-baseline justify-between border-t border-gray-200 pt-4">
                <div className="text-xs uppercase tracking-wider text-gray-400">Budapest defekt</div>
                <div className="font-display font-bold text-lg">25.000 Ft <span className="text-xs text-gray-400">+ ÁFA-tól</span></div>
              </div>
              <div className="flex items-baseline justify-between border-t border-gray-200 pt-4">
                <div className="text-xs uppercase tracking-wider text-gray-400">Autópálya defekt</div>
                <div className="font-display font-bold text-lg">35.000 Ft <span className="text-xs text-gray-400">+ ÁFA-tól</span></div>
              </div>
              <div className="flex items-baseline justify-between border-t border-gray-200 pt-4">
                <div className="text-xs uppercase tracking-wider text-gray-400">Budapesten kívül</div>
                <div className="font-display font-bold text-lg">+ 300 Ft<span className="text-xs text-gray-400">/km</span></div>
              </div>
            </div>

            <a
              href="#arkalkulator"
              data-testid="hero-card-cta"
              className="mt-8 group flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white rounded-sm transition-all glow-blue"
            >
              <span className="text-sm font-bold uppercase tracking-wider">Számold ki az árad</span>
              <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
